import React, { useEffect, useState } from 'react';
import lodash from 'lodash';
import { FormItemWrap, api } from 'react-structure-admin';
import {
  Col,
  DatePicker,
  Form,
  Row,
  Modal,
  Divider,
  Alert,
  message
} from 'antd';
import {
  FormItemUserDetail,
  InputCurrency,
  SelectBoolean,
  SelectPaymentMethod
} from 'components';
import {
  BANKCHECK,
  BILLETBANK,
  CREDITCARD,
  DEBITCARD,
  PAYMENT,
  RECEIPT
} from 'constants/types';
import RemoteSelect from 'components/RemoteSelect';
import moment from 'moment';
import useAuth from 'core/hooks/useAuth';

const FinancialEntryFormPaymentItem = ({
  onClose,
  field,
  type,
  form,
  disabled = false,
  showAudit = false
}) => {
  const [drawerForm] = Form.useForm();
  const selectRef = React.useRef(null);
  const [checkingAccount, setCheckingAccount] = useState(null);
  const initialValues = form.getFieldValue(['items', field.name]);
  const { accountantId } = useAuth();
  const typeLabel = type === RECEIPT ? 'recebimento' : 'pagamento';

  const getLabelPaymentMethod = () => {
    return type === RECEIPT ? 'Forma de recebimento' : 'Forma de pagamento';
  };

  const hasChangedAmount = (values) => {
    return (
      lodash.get(values, 'payment.interestAmount') !== undefined ||
      lodash.get(values, 'payment.discount') !== undefined ||
      lodash.get(values, 'payment.fineAmount') !== undefined
    );
  };

  const handleSelectCheckingAccount = (newCheckingAccount) => {
    if (newCheckingAccount?.type === 'card') {
      if (newCheckingAccount.credicardType !== type) {
        message.error(
          `Conta do tipo cartão não pode ser usado para ${typeLabel}`
        );

        setTimeout(() => {
          drawerForm.setFieldsValue({ checkingAccount: null });
        }, 250);
        return;
      }
    }

    setCheckingAccount(newCheckingAccount);
  };

  const hasChangedPaymentMethodOrCheckingAccount = (values) => {
    return (
      lodash.get(values, 'checkingAccount') !== undefined ||
      lodash.get(values, 'payment.method') !== undefined ||
      lodash.get(values, 'payment.date') !== undefined
    );
  };

  const alertNotCredicard = () => {
    Modal.info({
      width: '700px',
      title: 'Conta não está configurada para usar cartão de crédito',
      content:
        'A conta selecionada não possui as informações do dia de fechamento e vencimento da fatura do cartão de crédito. Favor informar esses dados no cadastro da conta. A data disponível deve ser a data do vencimento da  fatura do cartão que essa despesa estará incluída.'
    });
  };

  const getReceiptNextBusinessDay = ({ method, date }) => {
    const nextBusinessDay = moment(date).clone();

    switch (method) {
      case BANKCHECK:
      case DEBITCARD:
        return nextBusinessDay.add(1, 'day');
      case BILLETBANK:
        return nextBusinessDay.add(2, 'day');
      case CREDITCARD:
        return nextBusinessDay.add(30, 'day');
      default:
        return nextBusinessDay;
    }
  };

  const getPaymentNextBusinessDay = ({ method, date }) => {
    const nextBusinessDay = moment(date).clone();

    if (method === CREDITCARD) {
      const { type, credicardInvoiceClosingDay, credicardInvoiceDueDate } =
        checkingAccount ?? {};

      if (!(type === 'bankAccount')) {
        return nextBusinessDay.add(30, 'day');
      }

      if (!(type === 'card')) {
        alertNotCredicard();
        return nextBusinessDay.add(30, 'day');
      }

      const invoiceClosing = nextBusinessDay
        .clone()
        .date(credicardInvoiceClosingDay);

      const invoiceDueDate = nextBusinessDay
        .clone()
        .date(credicardInvoiceDueDate);

      const newDueDate = moment(invoiceDueDate).clone();

      if (newDueDate > date && date <= invoiceClosing) {
        return newDueDate.date(credicardInvoiceDueDate);
      }

      return newDueDate.add(1, 'month').date(credicardInvoiceDueDate);
    }

    switch (method) {
      case BANKCHECK:
        return nextBusinessDay.add(1, 'day');
      default:
        return nextBusinessDay;
    }
  };

  const getNextBusinessDay = ({ payment, dueDate }) => {
    let nextBusinessDay = null;

    if (type === RECEIPT) {
      nextBusinessDay = getReceiptNextBusinessDay(payment);
    }

    if (type === PAYMENT) {
      nextBusinessDay = getPaymentNextBusinessDay(payment, dueDate);
    }

    if (nextBusinessDay) {
      api
        .get(`holidays/next-business-day`, nextBusinessDay.toISOString())
        .then(({ data }) => {
          if (data?.result) {
            drawerForm.setFields([
              {
                name: ['payment', 'dateOfCompensation'],
                value: moment(data?.result)
              }
            ]);
          }
        });
    }
  };

  const checkIsPaymentMethodAllowedForCheckingAccount = (
    changedValues,
    item
  ) => {
    const { checkingAccount, payment } = item;

    if (!(checkingAccount && payment.method)) {
      return;
    }

    const changedCheckingAccount =
      lodash.get(changedValues, 'checkingAccount') !== undefined;
    const changedPaymentMethod =
      lodash.get(changedValues, 'payment.method') !== undefined;
    const changedPaymentDate =
      lodash.get(changedValues, 'payment.date') !== undefined;

    if (changedCheckingAccount || changedPaymentMethod) {
      api
        .post(
          `checking-accounts/${checkingAccount?.key}/validate-payment-methods/${payment.method}`
        )
        .then(({ data }) => {
          if (!data?.result) {
            Modal.confirm({
              width: '700px',
              cancelText: 'Voltar',
              okText: 'Continuar',
              afterClose: () => {
                selectRef.current.focus();
              },
              onCancel: () => {
                if (changedCheckingAccount) {
                  setCheckingAccount(null);
                  drawerForm.setFieldsValue({ checkingAccount: null });
                }

                if (changedPaymentMethod) {
                  drawerForm.setFieldsValue({ payment: { method: null } });
                }
              },

              onOk: () => {},
              title: `A forma de ${typeLabel} selecionada não é recomendada para usar nesta conta. Deseja prosseguir?`
            });
          }
        });
    }

    if ((changedPaymentMethod || changedPaymentDate) && item?.payment?.date) {
      getNextBusinessDay(item);
    }
  };

  const handleSubmit = () => {
    drawerForm.validateFields().then((values) => {
      form.setFields([
        {
          name: ['items', field.name],
          value: {
            ...initialValues,
            ...values,
            checkingAccount: {
              ...values.checkingAccount,
              item: checkingAccount
            }
          }
        }
      ]);

      onClose();
    });
  };

  const handleChangeValues = (changedValues, values) => {
    if (hasChangedAmount(changedValues)) {
      const netAmount =
        values.amount +
        values.payment.interestAmount +
        values.payment.fineAmount -
        values.payment.discount;

      drawerForm.setFieldsValue({ payment: { netAmount } });
    }

    if (!hasChangedPaymentMethodOrCheckingAccount(changedValues)) {
      return;
    }

    checkIsPaymentMethodAllowedForCheckingAccount(changedValues, values);
  };

  const handleTriggerClose = () => {
    if (disabled) {
      onClose();
      return;
    }

    Modal.confirm({
      width: '700px',
      cancelText: 'Voltar',
      okText: 'Sim',
      onOk: () => {
        form.setFields([
          {
            name: ['items', field.name, 'isPaid'],
            value: false
          }
        ]);
        onClose();
      },
      title: 'Deseja cancelar o pagamento dessa parcela?'
    });
  };

  useEffect(() => {
    if (!checkingAccount) {
      setCheckingAccount(initialValues?.checkingAccount?.item);
    }
  }, []);

  const handleBeforeBinding = (values) => {
    if (!values.payment.date) {
      values.payment.date = moment();
    }

    return values;
  };

  return (
    <Modal
      visible
      width={700}
      title={type === RECEIPT ? 'Recebimento' : 'Pagamento'}
      onCancel={handleTriggerClose}
      okText="Salvar"
      onOk={handleSubmit}
      closable={disabled}
      footer={disabled ? null : undefined}
    >
      <Form
        form={drawerForm}
        layout="vertical"
        initialValues={handleBeforeBinding(initialValues)}
        onValuesChange={handleChangeValues}
        preserve
        validateTrigger={false}
      >
        <Form.Item name="dueDate" noStyle />
        <Form.Item name="installmentNumber" noStyle />
        <Form.Item name="amount" noStyle />

        {accountantId && checkingAccount?.ledgerAccount === null && (
          <Alert
            className="gx-mb-1"
            showIcon
            description="Conta contábil não configurada para a conta informada. Favor contactar a contabilidade."
            type="warning"
            closable
          />
        )}

        <Row className="no-padding-last">
          <Col span={12}>
            <FormItemWrap
              name="checkingAccount"
              label="Conta"
              required
              autoFocus
            >
              <RemoteSelect
                resource="checking-accounts"
                allowClear={false}
                onSelect={handleSelectCheckingAccount}
                disabled={disabled}
              />
            </FormItemWrap>
          </Col>
          <Col span={12}>
            <FormItemWrap
              name={['payment', 'method']}
              label={getLabelPaymentMethod()}
              required
            >
              <SelectPaymentMethod disabled={disabled} ref={selectRef} />
            </FormItemWrap>
          </Col>
        </Row>
        <Row className="no-padding-last">
          <Col span={12}>
            <FormItemWrap
              name={['payment', 'date']}
              label={
                type === RECEIPT ? 'Data de recebimento' : 'Data de pagamento'
              }
              required
            >
              <DatePicker
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(date) => date.isAfter(moment(), 'day')}
                disabled={disabled}
              />
            </FormItemWrap>
          </Col>

          <Col span={12}>
            <Form.Item
              name={['payment', 'dateOfCompensation']}
              label="Data disponível"
              tooltip={{
                title:
                  'Data que o valor será creditado/debitado na conta informada.'
              }}
              rules={[{ type: 'date', required: true }]}
            >
              <DatePicker
                format="DD/MM/YYYY"
                allowClear={false}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[6, 0]}>
          <Col span={5}>
            <FormItemWrap
              className="ant-form-item-money"
              name="amount"
              label="Valor"
            >
              <InputCurrency disabled />
            </FormItemWrap>
          </Col>

          <Col span={5}>
            <FormItemWrap
              className="ant-form-item-money"
              name={['payment', 'interestAmount']}
              label="Juros"
              required
            >
              <InputCurrency disabled={disabled} />
            </FormItemWrap>
          </Col>

          <Col span={4}>
            <FormItemWrap
              className="ant-form-item-money"
              name={['payment', 'fineAmount']}
              label="Multa"
              required
            >
              <InputCurrency disabled={disabled} />
            </FormItemWrap>
          </Col>

          <Col span={5}>
            <FormItemWrap
              className="ant-form-item-money"
              name={['payment', 'discount']}
              label="Desconto"
              required
            >
              <InputCurrency disabled={disabled} />
            </FormItemWrap>
          </Col>

          <Col span={5}>
            <FormItemWrap
              className="ant-form-item-money"
              name={['payment', 'netAmount']}
              label={type === RECEIPT ? 'Valor recebido' : 'Valor pago'}
              rules={[
                {
                  type: 'number',
                  min: 0.0,
                  message: 'Valor recebido não poder ser menor que 0.'
                }
              ]}
            >
              <InputCurrency disabled />
            </FormItemWrap>
          </Col>
        </Row>
        {showAudit && (
          <>
            <Divider orientation="left">Auditoria</Divider>
            <Row gutter={[15, 0]}>
              <Col flex={1}>
                <FormItemWrap
                  name={['payment', 'creationTime']}
                  label={
                    type === RECEIPT
                      ? 'Data de recebimento'
                      : 'Data de pagamento'
                  }
                >
                  <DatePicker
                    format="DD/MM/YYYY HH:MM"
                    allowClear={false}
                    disabled={disabled}
                    placeholder=""
                  />
                </FormItemWrap>
              </Col>
              <Col>
                <FormItemWrap
                  name={['payment', 'user']}
                  label={
                    type === RECEIPT
                      ? 'Usuário de recebimento'
                      : 'Usuário de pagamento'
                  }
                >
                  <FormItemUserDetail />
                </FormItemWrap>
              </Col>
            </Row>
          </>
        )}

        {showAudit && initialValues.exportAudit && (
          <>
            <Divider orientation="left">Auditoria contábil</Divider>
            <Row>
              <Col span={7}>
                <FormItemWrap
                  name={['exportAudit', 'date']}
                  label="Data da contabilização"
                >
                  <DatePicker format="DD/MM/YYYY HH:mm" disabled />
                </FormItemWrap>
              </Col>

              <Col span={7}>
                <FormItemWrap name={['exportAudit', 'user']} label="Usuário">
                  <FormItemUserDetail />
                </FormItemWrap>
              </Col>
              <Col span={10}>
                <FormItemWrap
                  name={['exportAudit', 'isModified']}
                  label="Alterado após contabilização?"
                >
                  <SelectBoolean disabled />
                </FormItemWrap>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default FinancialEntryFormPaymentItem;
